/**
 * strikeMapBuilder.ts — MER Algo Footprints v7.1B — Phase 6
 *
 * Aggregates normalized option contracts into per-strike level objects.
 * Pure function — no side effects.
 *
 * Placement: src/domains/market/engines/gamma/strikeMapBuilder.ts
 */

import type { NormalizedOptionContract, StrikeLevel } from './gammaTypes';

// ─── GEX proxy computation ────────────────────────────────────────────────────

/**
 * Gamma exposure proxy for one leg.
 * If real per-contract gamma is available: OI × gamma (direction-adjusted).
 * Otherwise: OI × 0.03 (a conservative flat gamma assumption for near-money strikes).
 * Explicitly named "proxy" — not a dealer-perfect GEX calculation.
 */
function gexProxy(oi: number, gamma: number | null): number {
  const g = gamma !== null ? gamma : 0.03;
  return oi * g;
}

// ─── Net pressure ─────────────────────────────────────────────────────────────

/**
 * Returns normalized net pressure: +1 if all calls, -1 if all puts, 0 if balanced.
 * Uses OI-weighted directional score bounded to [-1, 1].
 */
function computeNetPressure(callOI: number, putOI: number): number {
  const total = callOI + putOI;
  if (total === 0) return 0;
  return (callOI - putOI) / total;
}

// ─── Build strike map ─────────────────────────────────────────────────────────

/**
 * Aggregates contracts into a Map keyed by strike price.
 * Call and put legs are merged per strike.
 */
export function buildStrikeMap(
  chain: NormalizedOptionContract[],
  spot: number,
): StrikeLevel[] {
  if (!chain.length || spot <= 0) return [];

  // Accumulate raw values per strike
  const acc = new Map<number, {
    strike:       number;
    callOI:       number;
    putOI:        number;
    callVol:      number;
    putVol:       number;
    callGamma:    number | null;   // best gamma from any call leg at this strike
    putGamma:     number | null;
  }>();

  for (const c of chain) {
    if (!acc.has(c.strike)) {
      acc.set(c.strike, {
        strike: c.strike,
        callOI: 0, putOI: 0, callVol: 0, putVol: 0,
        callGamma: null, putGamma: null,
      });
    }
    const entry = acc.get(c.strike)!;
    if (c.optionType === 'call') {
      entry.callOI  += c.openInterest;
      entry.callVol += c.volume;
      if (c.gamma !== null && (entry.callGamma === null || c.gamma > entry.callGamma)) {
        entry.callGamma = c.gamma;
      }
    } else {
      entry.putOI  += c.openInterest;
      entry.putVol += c.volume;
      if (c.gamma !== null && (entry.putGamma === null || c.gamma > entry.putGamma)) {
        entry.putGamma = c.gamma;
      }
    }
  }

  // Compute totalOI for normalization
  let totalOISum = 0;
  for (const v of acc.values()) {
    totalOISum += v.callOI + v.putOI;
  }
  const totalOINorm = totalOISum > 0 ? totalOISum : 1;

  const levels: StrikeLevel[] = [];

  for (const v of acc.values()) {
    const totalOI       = v.callOI + v.putOI;
    const callGEXProxy  = gexProxy(v.callOI, v.callGamma);
    const putGEXProxy   = gexProxy(v.putOI,  v.putGamma);
    const netGEXProxy   = callGEXProxy - putGEXProxy;
    const netPressure   = computeNetPressure(v.callOI, v.putOI);
    const distFromSpotPct = ((v.strike - spot) / spot) * 100;

    // Magnet score: how much does this strike concentrate OI vs total?
    // High totalOI concentration → price gravitates toward this strike
    const magnetScore = Math.min(1, totalOI / totalOINorm);

    // Resistance: call-side OI above spot → upward movement is resisted
    const aboveSpot    = v.strike > spot;
    const resistanceScore = aboveSpot
      ? Math.min(1, v.callOI / totalOINorm)
      : 0;

    // Support: put-side OI below spot → downward movement is cushioned
    const belowSpot    = v.strike < spot;
    const supportScore = belowSpot
      ? Math.min(1, v.putOI / totalOINorm)
      : 0;

    levels.push({
      strike:          v.strike,
      distFromSpotPct,
      callOI:          v.callOI,
      putOI:           v.putOI,
      totalOI,
      callVol:         v.callVol,
      putVol:          v.putVol,
      callGEXProxy,
      putGEXProxy,
      netGEXProxy,
      netPressure,
      magnetScore,
      resistanceScore,
      supportScore,
    });
  }

  return levels.sort((a, b) => a.strike - b.strike);
}
